/**
 * =============================================================================
 * WlRecentContractsCard
 *
 * Card do painel WL → Financeiro que substitui "Cobranças recentes" por
 * "Contratações recentes" — lista as últimas contratações de PLANOS e
 * ADD-ONS de todos os tenants (estrutura híbrida: legado + Planos 2.0).
 *
 * - Paginação server-side (30/pág) via RPC `wl_list_recent_contracts` v1.0.0
 * - Filtro local: Todos / Planos / Add-ons
 * - Reaproveita o filtro global de status do dashboard
 * - Tenants cortesia aparecem com MRR = R$ 0 e badge dedicado
 *
 * @version 1.1.0
 * RELEASE: 2026-07-22 v1.1.0 - Coluna "Contratado em" com hora/minuto, badge
 *          "Tipo" sem quebra de linha e clique na linha abrindo o
 *          WlTenantDetailsModal (drill-down cross-tenant).
 * RELEASE: 2026-07-22 v1.0.0 - Versao inicial (substitui WlRecentChargesCard).
 * =============================================================================
 */
import { useState } from 'react';
import { FileSignature } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import {
  useWlRecentContracts,
  type WlContractType,
} from '@/hooks/wl/useWlRecentContracts';
import { WlTenantDetailsModal } from '@/components/wl/WlTenantDetailsModal';
import { cn } from '@/lib/utils';

const PAGE_SIZE_DEFAULT = 30;

/** Formata BRL, tolerante a null. */
const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(v || 0),
  );

/** Formata data ISO em pt-BR com hora/minuto (dd/MM/yyyy HH:mm). */
const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    // Data + hora local do navegador, formato curto pt-BR.
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

/** Rótulo amigável para a recorrência. */
const recurrenceLabel = (r: string | null) => {
  if (!r) return '—';
  const norm = r.toLowerCase();
  if (norm.startsWith('year') || norm.startsWith('anu')) return 'Anual';
  if (norm.startsWith('month') || norm.startsWith('mens')) return 'Mensal';
  return r;
};

interface Props {
  /** Status efetivos vindos do filtro global do dashboard. */
  statuses?: string[];
}

/**
 * Card "Contratações recentes" — cross-tenant, uma linha por contrato.
 */
export function WlRecentContractsCard({ statuses }: Props) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [typeFilter, setTypeFilter] = useState<WlContractType | null>(null);
  // Drill-down: tenant selecionado abre WlTenantDetailsModal por cima do card.
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  const { data, isLoading } = useWlRecentContracts({
    page,
    pageSize,
    type: typeFilter,
    statuses: statuses && statuses.length ? statuses : null,
    includeCourtesy: true,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  /** Botão pill do filtro local (Todos / Planos / Add-ons). */
  const PillBtn = ({
    value,
    label,
  }: {
    value: WlContractType | null;
    label: string;
  }) => {
    const active = typeFilter === value;
    return (
      <Button
        size="sm"
        variant={active ? 'default' : 'outline'}
        className={cn('h-7 px-2.5 text-xs')}
        onClick={() => {
          setTypeFilter(value);
          setPage(1);
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-muted-foreground" />
            Contratações recentes
          </CardTitle>
          <span className="text-[11px] text-muted-foreground">
            Últimas contratações de planos e add-ons — {total} registro{total === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <PillBtn value={null} label="Todos" />
          <PillBtn value="plan" label="Planos" />
          <PillBtn value="addon" label="Add-ons" />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-[420px] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="text-xs font-medium">Tenant</TableHead>
                <TableHead className="text-xs font-medium">Tipo</TableHead>
                <TableHead className="text-xs font-medium">Item</TableHead>
                <TableHead className="text-xs font-medium">Recorrência</TableHead>
                <TableHead className="text-xs font-medium text-right">Qtde</TableHead>
                <TableHead className="text-xs font-medium text-right">Valor unit.</TableHead>
                <TableHead className="text-xs font-medium text-right">MRR</TableHead>
                <TableHead className="text-xs font-medium">Integração</TableHead>
                <TableHead className="text-xs font-medium">Contratado em</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center py-6 text-sm text-muted-foreground"
                  >
                    Carregando…
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center py-6 text-sm text-muted-foreground"
                  >
                    Nenhuma contratação encontrada
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow
                    key={`${row.contract_type}-${row.id}`}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setSelectedTenantId(row.tenant_id)}
                    title="Abrir detalhes do tenant"
                  >
                    <TableCell className="py-2 text-xs font-medium">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{row.tenant_name}</span>
                        {row.is_courtesy && (
                          <Badge
                            variant="outline"
                            className="text-[9px] font-medium border-amber-500/40 text-amber-600 dark:text-amber-400"
                          >
                            Cortesia
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px] font-medium whitespace-nowrap',
                          row.contract_type === 'plan'
                            ? 'border-primary/40 text-primary'
                            : 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400',
                        )}
                      >
                        {row.contract_type === 'plan' ? 'Plano' : 'Add-on'}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2 text-xs">{row.item_name}</TableCell>
                    <TableCell className="py-2 text-xs text-muted-foreground">
                      {recurrenceLabel(row.recurrence_interval)}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-right">
                      {row.quantity}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-right">
                      {fmtBRL(row.unit_price)}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-right font-medium">
                      {row.is_courtesy ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        fmtBRL(row.mrr_impact)
                      )}
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px] font-medium',
                          row.billing_integration === 'integracao_v1'
                            ? 'border-blue-500/40 text-blue-600 dark:text-blue-400'
                            : 'border-muted-foreground/30 text-muted-foreground',
                        )}
                      >
                        {row.billing_integration === 'integracao_v1' ? 'V1' : 'Legado'}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2 text-xs whitespace-nowrap">
                      {fmtDate(row.contracted_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {total > pageSize && (
          <PaginationFooter
            totalItems={total}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(sz) => {
              setPageSize(sz);
              setPage(1);
            }}
            pageSizeOptions={[15, 30, 50]}
          />
        )}
      </CardContent>

      {/* Drill-down: modal de detalhes do tenant selecionado. */}
      <WlTenantDetailsModal
        tenantId={selectedTenantId}
        open={!!selectedTenantId}
        onClose={() => setSelectedTenantId(null)}
      />
    </Card>
  );
}
